import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, effect, inject, untracked, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { GlobalTimerService } from '@shared/services/time/global-timer.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { NotificationService } from '@shared/services/notificacion/notification.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppConfig } from '@env/app-config';

interface TimerTaskOption { id: number; title: string; code: string | null; }

/**
 * Widget persistente del timer activo (RF-010): visible en el header en CUALQUIER pantalla
 * mientras el usuario tenga un temporizador corriendo, no solo dentro del panel de la tarea —
 * ambos comparten el mismo `GlobalTimerService`, así que siempre muestran el mismo estado.
 */
@Component({
  selector: 'app-active-timer-widget',
  standalone: true,
  imports: [RouterLink, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`:host { display: flex; flex: 0 0 auto; align-items: center; }`],
  template: `
    <div class="flex w-max max-w-full flex-nowrap items-center gap-1">
      <div class="relative shrink-0">
      <button type="button" class="h-8 w-8 flex items-center justify-center rounded-lg text-xs text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-white/10"
              (click)="toggleSearch()" aria-label="Buscar tarea para iniciar temporizador">
        <i class="bi bi-search" aria-hidden="true"></i>
      </button>
      @if (searchOpen()) {
        <div class="absolute left-0 top-full z-[100] mt-1 w-[min(22.5rem,calc(100vw_-_1rem))] max-w-[calc(100vw_-_1rem)] max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 shadow-xl xl:left-auto xl:right-0 xl:top-full xl:max-h-[70vh]">
          <input class="w-full rounded border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-sm"
                 placeholder="Buscar en mis tareas..." [value]="query()" (input)="search(($any($event.target)).value)" autofocus />
          <p class="px-3 pt-2 text-xs text-gray-500">Al seleccionar una tarea, se iniciará el contador de tiempo.</p>
          @for (task of results(); track task.id) {
            <button type="button" class="block w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                    (click)="startTask(task)">
              <span class="font-medium">{{ task.title }}</span>
              @if (task.code) { <span class="ml-2 text-xs text-gray-500">{{ task.code }}</span> }
            </button>
          }
          @if (query().length >= 2 && !results().length) { <p class="px-3 py-2 text-xs text-gray-500">Sin tareas asignadas coincidentes</p> }
        </div>
      }
      </div>
    @if (timerService.activeTimer(); as timer) {
      <div class="flex min-w-0 shrink items-center gap-1 rounded-lg bg-brand-50 dark:bg-brand-500/10 pl-1 pr-1"
           role="group" [attr.aria-label]="'layout.activeTimer.label' | transloco">
        <a [routerLink]="timer.taskId ? ['/nexus/tareas', timer.taskId] : null"
           class="flex min-w-0 items-center gap-1.5 h-8 px-1.5 rounded-lg text-xs font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-white/10 max-w-[8rem] sm:max-w-[14rem]"
           [title]="displayName()"
           [attr.aria-label]="('layout.activeTimer.goToTask' | transloco: { task: displayName() })">
          <i class="bi bi-stopwatch flex-shrink-0" aria-hidden="true"></i>
          <span class="truncate">{{ displayName() }}</span>
        </a>
        <!-- El timer activo es uno por usuario en TODO el sistema (RF-002), no por empresa: si es
             de otra empresa distinta de la que el usuario tiene abierta, se lo decimos aquí — de
             lo contrario parecería que es de la empresa actual. -->
        @if (otherCompanyName(); as empresa) {
          <span class="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-100 dark:bg-white/10 text-brand-700 dark:text-brand-300 max-w-[7rem] truncate"
                [title]="('layout.activeTimer.otherCompany' | transloco: { company: empresa })">
            {{ empresa }}
          </span>
        }
        <!-- El contador tiquea cada segundo: sin aria-live, para no obligar al lector de
             pantalla a re-anunciarlo constantemente. El grupo de arriba ya lo introduce una vez. -->
        <span class="font-mono tabular-nums text-xs text-brand-700 dark:text-brand-300 px-0.5" aria-hidden="true">
          {{ timerService.elapsedLabel() }}
        </span>
        <button type="button"
                class="w-7 h-7 flex items-center justify-center rounded-lg text-error-500 hover:bg-error-50 dark:hover:bg-white/10 flex-shrink-0"
                [title]="'layout.activeTimer.stop' | transloco"
                [attr.aria-label]="'layout.activeTimer.stop' | transloco"
                (click)="timerService.stop()">
          <i class="bi bi-stop-fill text-sm" aria-hidden="true"></i>
        </button>
      </div>
    }
    </div>
  `,
})
export class ActiveTimerWidgetComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  readonly timerService = inject(GlobalTimerService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly notification = inject(NotificationService);
  private readonly http = inject(HttpClient);
  readonly searchOpen = signal(false);
  readonly query = signal('');
  readonly results = signal<TimerTaskOption[]>([]);
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly transloco = inject(TranslocoService);
  private readonly activeLang = toSignal(this.transloco.langChanges$, { initialValue: this.transloco.getActiveLang() });

  /** Código + título de la tarea, o un texto genérico si el timer no tiene tarea asociada. */
  readonly displayName = computed(() => {
    this.activeLang(); // dependencia reactiva: el texto de respaldo cambia con el idioma
    const timer = this.timerService.activeTimer();
    if (!timer) return '';
    if (!timer.taskTitle) return this.transloco.translate('layout.activeTimer.noTask');
    return timer.taskCode ? `${timer.taskCode} · ${timer.taskTitle}` : timer.taskTitle;
  });

  /** Nombre de la empresa del timer SOLO si es distinta de la que el usuario tiene abierta. */
  readonly otherCompanyName = computed(() => {
    const timer = this.timerService.activeTimer();
    if (!timer || timer.companyId === this.companyContext.companyId()) return null;
    return timer.companyName;
  });

  constructor() {
    // El aviso de "se detuvo X al iniciar este" es un evento (no estado a pintar aquí), así que
    // se consume como toast y se limpia — este widget está montado siempre, es el sitio natural
    // para mostrarlo sin importar en qué pantalla esté el usuario.
    effect(() => {
      const stopped = this.timerService.stoppedNotice();
      if (!stopped) return;
      untracked(() => {
        const nombre = stopped.taskTitle
          ? (stopped.taskCode ? `${stopped.taskCode} · ${stopped.taskTitle}` : stopped.taskTitle)
          : null;
        const mensaje = nombre
          ? this.transloco.translate('layout.activeTimer.stoppedNoticeTask', { task: nombre })
          : this.transloco.translate('layout.activeTimer.stoppedNoticeCompany', { company: stopped.companyName ?? '' });
        this.notification.showInfo(this.transloco.translate('layout.activeTimer.stoppedNoticeTitle'), mensaje);
        this.timerService.clearStoppedNotice();
      });
    });
  }

  search(value: string): void {
    this.query.set(value);
    this.results.set([]);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (value.trim().length < 2) return;
    this.searchTimeout = setTimeout(() => {
      const companyId = this.companyContext.companyId();
      if (companyId) this.http.get<TimerTaskOption[]>(`${AppConfig.apiUrl}/api/nexus/tasks/timer-search`, {
        params: new HttpParams().set('companyId', companyId).set('q', value.trim()),
      }).subscribe(items => this.results.set(items));
    }, 250);
  }

  toggleSearch(): void {
    this.searchOpen.update(current => !current);
  }

  @HostListener('document:click', ['$event'])
  closeSearchOnOutsideClick(event: MouseEvent): void {
    if (this.searchOpen() && !this.host.nativeElement.contains(event.target as Node)) {
      this.searchOpen.set(false);
    }
  }

  startTask(task: TimerTaskOption): void {
    this.timerService.start(task.id);
    this.searchOpen.set(false);
    this.query.set('');
    this.results.set([]);
  }
}
